import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionsCollectionUpdateRequest,
  CollectionsCreateCollectionRequest,
  CollectionsSignCollectionResponse,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { invalidateCollectionLists, removeCollectionCache, setUpdatedCollectionCache } from './cache';
import { parseCollection, type Collection } from './entity';

/* ------------------------------------------------------------------------- */
/* create                                                                     */
/* ------------------------------------------------------------------------- */

type CreateCollectionBody = CollectionsCreateCollectionRequest['collection'];

export interface CreateCollectionInput extends CreateCollectionBody {
  workspaceId: number;
}

/** @public */
export async function createCollection(input: CreateCollectionInput): Promise<Collection> {
  const { workspaceId, ...collection } = input;
  const res = await hydraUiApi.collections.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: { collection },
  });
  if (res.collection == null) {
    throw new Error('collection missing from create-collection response');
  }
  return parseCollection(res.collection);
}

type UseCreateCollectionVariables = Omit<CreateCollectionInput, 'workspaceId'>;

interface UseCreateCollectionOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Collection, UseCreateCollectionVariables>;
}

/** @public */
export const useCreateCollection = ({ workspaceId: override, mutationConfig }: UseCreateCollectionOptions = {}) => {
  const workspaceId = override ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateCollectionVariables) => createCollection({ workspaceId, ...variables }),
    onSuccess: (created, ...args) => {
      invalidateCollectionLists(qc, workspaceId);
      onSuccess?.(created, ...args);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* update                                                                     */
/* ------------------------------------------------------------------------- */

export interface UpdateCollectionInput {
  workspaceId: number;
  id: string;
  // Kept whole (not spread) so the update_type discriminated union narrows.
  update: CollectionsCollectionUpdateRequest;
}

/** @public */
export async function updateCollection(input: UpdateCollectionInput): Promise<Collection> {
  const res = await hydraUiApi.collections.update({
    params: { path: { environment_id: String(input.workspaceId), id: input.id } },
    body: { collection: input.update },
  });
  if (res.collection == null) {
    throw new Error(`collection ${input.id} missing from update-collection response`);
  }
  return parseCollection(res.collection);
}

type UseUpdateCollectionVariables = Omit<UpdateCollectionInput, 'workspaceId'>;

interface UseUpdateCollectionOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Collection, UseUpdateCollectionVariables>;
}

/** @public */
export const useUpdateCollection = ({ workspaceId: override, mutationConfig }: UseUpdateCollectionOptions = {}) => {
  const workspaceId = override ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseUpdateCollectionVariables) => updateCollection({ workspaceId, ...variables }),
    onSuccess: (updated, variables, ...args) => {
      setUpdatedCollectionCache(qc, workspaceId, updated, variables.update.update_type);
      onSuccess?.(updated, variables, ...args);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* delete                                                                     */
/* ------------------------------------------------------------------------- */

export interface DeleteCollectionInput {
  workspaceId: number;
  id: string;
}

/** @public */
export async function deleteCollection(input: DeleteCollectionInput): Promise<void> {
  await hydraUiApi.collections.delete({
    params: { path: { environment_id: String(input.workspaceId), id: input.id } },
  });
}

type UseDeleteCollectionVariables = Omit<DeleteCollectionInput, 'workspaceId'>;

interface UseDeleteCollectionOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteCollectionVariables>;
}

/** @public */
export const useDeleteCollection = ({ workspaceId: override, mutationConfig }: UseDeleteCollectionOptions = {}) => {
  const workspaceId = override ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteCollectionVariables) => deleteCollection({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...args) => {
      removeCollectionCache(qc, workspaceId, variables.id);
      invalidateCollectionLists(qc, workspaceId);
      onSuccess?.(result, variables, ...args);
    },
  });
};

/* ------------------------------------------------------------------------- */
/* sign (signed-upload URL)                                                   */
/* ------------------------------------------------------------------------- */

export interface SignCollectionInput {
  workspaceId: number;
  size: string;
  type: string;
}

/** @public */
export async function signCollection(input: SignCollectionInput): Promise<CollectionsSignCollectionResponse> {
  return hydraUiApi.collections.sign({
    params: { path: { environment_id: String(input.workspaceId) } },
    body: { size: input.size, type: input.type },
  });
}

type UseSignCollectionVariables = Omit<SignCollectionInput, 'workspaceId'>;

interface UseSignCollectionOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<CollectionsSignCollectionResponse, UseSignCollectionVariables>;
}

/** @public */
export const useSignCollection = ({ workspaceId: override, mutationConfig }: UseSignCollectionOptions = {}) => {
  const workspaceId = override ?? requireWorkspaceId();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseSignCollectionVariables) => signCollection({ workspaceId, ...variables }),
    onSuccess,
  });
};
